/**
 * The customer's quote (docs/10-quote-engine.md §6): rendered from the frozen
 * snapshot, in the installer's branding. Used by the public page
 * (go.<domain>/q/<token>, pages/quotes/ProposalPage.tsx) and, read-only, by the
 * builder's preview, so the installer previews exactly what the customer sees.
 *
 * The customer compares the options, adds optional extras, picks a tariff for
 * the running costs and (if offered) finance, then accepts with their typed name.
 * Prices here are display sums of snapshot figures; the public API re-prices the
 * choice from the same snapshot when it's accepted.
 */
import { useMemo, useState, type CSSProperties, type FormEvent, type ReactNode } from "react";
import { Check, ChevronDown, Download, Flame, Home, Leaf, Mail, Phone, PoundSterling, ShieldCheck, Thermometer, Zap } from "lucide-react";
import type { Acceptance, Proposal, ProposalOption, QuoteStatus } from "./api";
import { day, gbp, label, monthlyRepayment, FORMS, FUELS, KINDS } from "./format";
import "./proposal.css";

export interface AcceptChoice {
  optionId: string;
  extras: { extraId: string; qty: number }[];
  tariffId: string | null;
  payment: "cash" | "finance";
  financeMonths: number | null;
  name: string;
  agree: boolean;
}

interface Props {
  proposal: Proposal;
  status?: QuoteStatus;
  expired?: boolean;
  acceptance?: Acceptance | null;
  declined?: boolean;
  /** Installer preview: everything shows, nothing can be accepted */
  preview?: boolean;
  onAccept?: (choice: AcceptChoice) => Promise<void>;
  onDecline?: (reason: string) => Promise<void>;
}

const HEX = /^#[0-9a-f]{6}$/i;

/** Readable text colour for a brand background. */
function textOn(hex: string): string {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return 0.299 * r + 0.587 * g + 0.114 * b > 170 ? "#202937" : "#ffffff";
}

export function ProposalView({ proposal: p, status, expired, acceptance, declined, preview, onAccept, onDecline }: Props) {
  const primary = HEX.test(p.brand.primary ?? "") ? p.brand.primary! : "#202937";
  const accent = HEX.test(p.brand.accent ?? "") ? p.brand.accent! : "#E1EE2B";
  const style = { "--pp-primary": primary, "--pp-on-primary": textOn(primary), "--pp-accent": accent, "--pp-on-accent": textOn(accent) } as CSSProperties;

  const available = p.options.filter((o) => o.available);
  const answered = status === "accepted" || status === "declined" || Boolean(acceptance) || Boolean(declined);
  const locked = answered || Boolean(expired);

  // The customer's choices (an accepted quote shows what was accepted)
  const [optionId, setOptionId] = useState<string>(acceptance?.optionId ?? p.recommendedId ?? available[0]?.id ?? "");
  const [extras, setExtras] = useState<Record<string, number>>(() =>
    Object.fromEntries((acceptance?.extras ?? []).map((x) => [x.extraId, x.qty])),
  );
  const [tariffId, setTariffId] = useState<string | null>(acceptance?.tariffId ?? p.runningCosts?.defaultTariffId ?? p.runningCosts?.tariffs[0]?.id ?? null);
  const [payment, setPayment] = useState<"cash" | "finance">(acceptance?.payment ?? "cash");
  const [months, setMonths] = useState<number | null>(acceptance?.financeMonths ?? p.finance?.termsMonths[p.finance.termsMonths.length - 1] ?? null);

  const option = available.find((o) => o.id === optionId) ?? available[0];

  // Totals for the chosen option + chosen optional extras (the same sums the API makes)
  const totals = useMemo(() => {
    if (!option?.totals) return null;
    let net = option.totals.net;
    let vat = option.totals.vat;
    for (const e of p.optionalExtras) {
      const q = extras[e.id] ?? 0;
      if (!q) continue;
      const n = Math.round(e.unitPrice * q * 100) / 100;
      net += n;
      vat += Math.round(n * e.vatRate) / 100;
    }
    const gross = Math.round((net + vat) * 100) / 100;
    const grant = Math.min(option.totals.grant, gross);
    return { net, vat, gross, grant, payable: Math.round((gross - grant) * 100) / 100 };
  }, [option, extras, p.optionalExtras]);

  const company = p.company;
  return (
    <div className="pp" style={style}>
      <header className="pp-header">
        <div className="pp-wrap pp-header-in">
          <div className="pp-brand">
            {p.brand.logoUrl ? <img src={p.brand.logoUrl} alt={company.companyName} /> : <span className="pp-brand-name">{company.companyName}</span>}
          </div>
          <div className="pp-contact">
            {company.phone && (
              <a href={`tel:${company.phone.replace(/\s/g, "")}`} className="pp-chip">
                <Phone size={15} aria-hidden="true" /> {company.phone}
              </a>
            )}
            {company.email && (
              <a href={`mailto:${company.email}?subject=${encodeURIComponent(`Quote ${p.reference}`)}`} className="pp-chip pp-chip-primary">
                <Mail size={15} aria-hidden="true" /> Email us
              </a>
            )}
          </div>
        </div>
      </header>

      <main className="pp-wrap pp-main">
        {preview && <div className="pp-banner pp-banner-info">Preview: this is what your customer sees. Accepting is switched off here.</div>}
        {status === "accepted" || acceptance ? (
          <div className="pp-banner pp-banner-ok">
            <Check size={18} aria-hidden="true" /> Accepted by {acceptance?.name} on {day(acceptance?.signedAt)}. {company.companyName} will be in touch about the next steps.
          </div>
        ) : status === "declined" || declined ? (
          <div className="pp-banner">You declined this quote. If you change your mind, just get in touch.</div>
        ) : expired ? (
          <div className="pp-banner pp-banner-warn">This quote expired on {day(p.validUntil)}. Please contact us for an updated quote.</div>
        ) : null}

        <section className="pp-hero">
          <div>
            <p className="pp-eyebrow">
              Heat pump quote {p.reference}
              {p.subjectToSurvey ? " · subject to survey" : ""}
            </p>
            <h1>{p.customer.name ? `Your heat pump quote, ${p.customer.name.split(" ")[0]}` : "Your heat pump quote"}</h1>
            <p className="pp-address">
              <Home size={16} aria-hidden="true" /> {p.address.line}
            </p>
            <p className="pp-dates">
              Issued {day(p.issuedAt)} · Valid until {day(p.validUntil)}
            </p>
          </div>
          <button type="button" className="pp-print" onClick={() => window.print()}>
            <Download size={15} aria-hidden="true" /> Save as PDF
          </button>
        </section>

        {p.subjectToSurvey && !answered && (
          <div className="pp-banner pp-survey">
            <ShieldCheck size={18} aria-hidden="true" />
            <span>
              <strong>Subject to a home survey.</strong> We'll confirm the design and price when we survey your home. If anything changes, you'll get an updated quote before you
              commit.
            </span>
          </div>
        )}

        {p.intro && <p className="pp-intro">{p.intro}</p>}

        <HomeFacts p={p} />

        {/* ── Options ─────────────────────────────────────────────────── */}
        <section className="pp-section">
          <h2>{available.length > 1 ? "Choose your system" : "Your system"}</h2>
          <div className={`pp-options pp-options-${Math.min(p.options.length, 3)}`}>
            {p.options.map((o) => (
              <OptionCard key={o.id} o={o} selected={o.id === option?.id} recommended={o.id === p.recommendedId && available.length > 1} disabled={locked && o.id !== option?.id} onSelect={() => !locked && setOptionId(o.id)} />
            ))}
          </div>
        </section>

        {option && totals && (
          <>
            {/* ── Price ─────────────────────────────────────────────── */}
            <section className="pp-section pp-grid-2">
              <div className="pp-card">
                <h2>What's in your price</h2>
                <p className="pp-sub">{option.name}</p>
                <PriceTable option={option} display={p.priceDisplay} />
                {p.optionalExtras.length > 0 && (
                  <div className="pp-extras">
                    <h3>Optional extras</h3>
                    {p.optionalExtras.map((e) => {
                      const q = extras[e.id] ?? 0;
                      return (
                        <div key={e.id} className={`pp-extra${q ? " is-on" : ""}`}>
                          <label className="pp-extra-main">
                            <input
                              type="checkbox"
                              checked={q > 0}
                              disabled={locked}
                              onChange={(ev) => setExtras((x) => ({ ...x, [e.id]: ev.target.checked ? e.defaultQty || 1 : 0 }))}
                            />
                            <span>
                              <strong>{e.name}</strong>
                              {e.description && <small>{e.description}</small>}
                            </span>
                          </label>
                          <div className="pp-extra-price">
                            {e.perUnit && q > 0 && !locked ? (
                              <span className="pp-stepper">
                                <button type="button" aria-label={`Fewer ${e.unitLabel || "items"}`} onClick={() => setExtras((x) => ({ ...x, [e.id]: Math.max(1, q - 1) }))}>
                                  −
                                </button>
                                <span>
                                  {q} {e.unitLabel ? (q === 1 ? e.unitLabel : `${e.unitLabel}s`) : ""}
                                </span>
                                <button type="button" aria-label={`More ${e.unitLabel || "items"}`} onClick={() => setExtras((x) => ({ ...x, [e.id]: Math.min(e.maxQty, q + 1) }))}>
                                  +
                                </button>
                              </span>
                            ) : null}
                            <span>
                              {e.perUnit ? `${gbp(e.unitPrice * (1 + e.vatRate / 100))} each` : gbp(e.unitPrice * (1 + e.vatRate / 100))}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="pp-card pp-total-card">
                <h2>Your price</h2>
                <dl className="pp-totals">
                  <div>
                    <dt>Total before VAT</dt>
                    <dd>{gbp(totals.net)}</dd>
                  </div>
                  <div>
                    <dt>VAT</dt>
                    <dd>{totals.vat ? gbp(totals.vat) : "£0 (0% on heat pump installations)"}</dd>
                  </div>
                  <div>
                    <dt>Total including VAT</dt>
                    <dd>{gbp(totals.gross)}</dd>
                  </div>
                  {totals.grant > 0 && (
                    <div className="pp-grant-row">
                      <dt>Boiler Upgrade Scheme grant</dt>
                      <dd>−{gbp(totals.grant)}</dd>
                    </div>
                  )}
                  <div className="pp-payable">
                    <dt>You pay</dt>
                    <dd>{gbp(totals.payable)}</dd>
                  </div>
                </dl>
                {p.deposit && (
                  <p className="pp-note">
                    Deposit on acceptance: <strong>{p.deposit.type === "fixed" ? gbp(p.deposit.value) : gbp((totals.payable * p.deposit.value) / 100)}</strong>
                    {p.deposit.note ? `. ${p.deposit.note}` : ""}
                  </p>
                )}
                {p.finance && <Finance finance={p.finance} payable={totals.payable} payment={payment} setPayment={setPayment} months={months} setMonths={setMonths} locked={locked} />}
              </div>
            </section>

            {/* ── Running costs ────────────────────────────────────── */}
            {p.runningCosts && option.runningCosts && <RunningCostsCard rc={option.runningCosts} tariffs={p.runningCosts.tariffs} tariffId={tariffId} setTariffId={setTariffId} note={p.runningCosts.note} />}
          </>
        )}

        {/* ── Heat loss (our "show the working" principle) ─────────── */}
        {p.heatLoss && <HeatLossCard hl={p.heatLoss} option={option} />}

        {p.grant && p.grant.amount > 0 && (
          <section className="pp-section pp-card">
            <h2>
              <ShieldCheck size={20} aria-hidden="true" /> Your {gbp(p.grant.amount)} Boiler Upgrade Scheme grant
            </h2>
            <p className="pp-sub">We apply for the grant for you and take it off your price. Ofgem confirms eligibility when we apply.</p>
            <ul className="pp-checks">
              {p.grant.checks
                .filter((c) => c.status !== "info")
                .map((c) => (
                  <li key={c.text} className={`is-${c.status}`}>
                    <Check size={15} aria-hidden="true" /> {c.detail}
                  </li>
                ))}
            </ul>
          </section>
        )}

        {(p.whatsIncluded.length > 0 || p.whyChooseUs.length > 0) && (
          <section className="pp-section pp-grid-2">
            {p.whatsIncluded.length > 0 && (
              <div className="pp-card">
                <h2>Included as standard</h2>
                <ul className="pp-ticks">
                  {p.whatsIncluded.map((w) => (
                    <li key={w}>
                      <Check size={16} aria-hidden="true" /> {w}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {p.whyChooseUs.length > 0 && (
              <div className="pp-card">
                <h2>Why {company.companyName}</h2>
                <ul className="pp-ticks">
                  {p.whyChooseUs.map((w) => (
                    <li key={w}>
                      <ShieldCheck size={16} aria-hidden="true" /> {w}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        {p.terms.text && (
          <details className="pp-section pp-card pp-terms">
            <summary>
              Terms and conditions <ChevronDown size={16} aria-hidden="true" />
            </summary>
            <div className="pp-terms-text">{p.terms.text}</div>
          </details>
        )}

        {/* ── Accept ────────────────────────────────────────────── */}
        {option && totals && !answered && !expired && (
          <AcceptPanel
            preview={preview}
            optionName={option.name}
            subjectToSurvey={Boolean(p.subjectToSurvey)}
            payable={totals.payable}
            acceptanceText={p.acceptanceText}
            onAccept={(name, agree) =>
              onAccept?.({
                optionId: option.id,
                extras: Object.entries(extras)
                  .filter(([, q]) => q > 0)
                  .map(([extraId, qty]) => ({ extraId, qty })),
                tariffId,
                payment,
                financeMonths: payment === "finance" ? months : null,
                name,
                agree,
              }) ?? Promise.resolve()
            }
            onDecline={onDecline}
          />
        )}
      </main>

      <footer className="pp-footer">
        <div className="pp-wrap pp-footer-in">
          <div>
            <strong>{company.companyName}</strong>
            {company.tagline && <div>{company.tagline}</div>}
            {company.address && <div>{company.address}</div>}
          </div>
          <div className="pp-footer-right">
            {[company.phone, company.email, company.website].filter(Boolean).map((v) => (
              <div key={v}>{v}</div>
            ))}
            {(company.companyNumber || company.vatNumber) && (
              <div className="pp-muted">{[company.companyNumber && `Company no. ${company.companyNumber}`, company.vatNumber && `VAT ${company.vatNumber}`].filter(Boolean).join(" · ")}</div>
            )}
            {company.privacyUrl && (
              <a href={company.privacyUrl} target="_blank" rel="noreferrer">
                Privacy policy
              </a>
            )}
            <div className="pp-muted pp-sent-with">Sent with xenquote</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ─── Pieces ──────────────────────────────────────────────────────────────────

function HomeFacts({ p }: { p: Proposal }) {
  const facts: [ReactNode, string, string][] = [];
  if (p.property.kind) facts.push([<Home key="h" size={18} />, "Your home", [label(FORMS, p.property.builtForm as never), label(KINDS, p.property.kind as never).toLowerCase()].filter(Boolean).join(" ")]);
  if (p.property.floorArea) facts.push([<Home key="a" size={18} />, "Floor area", `${p.property.floorArea} m²`]);
  if (p.property.epc?.band) facts.push([<Leaf key="e" size={18} />, "EPC rating", p.property.epc.band]);
  if (p.property.fuel) facts.push([<Flame key="f" size={18} />, "Heating today", label(FUELS, p.property.fuel as never)]);
  if (p.heatLoss) facts.push([<Thermometer key="t" size={18} />, "Heat loss", `${p.heatLoss.kw} kW`]);
  if (!facts.length) return null;
  return (
    <section className="pp-facts">
      {facts.map(([icon, k, v]) => (
        <div key={k} className="pp-fact">
          <span className="pp-fact-icon" aria-hidden="true">
            {icon}
          </span>
          <span>
            <small>{k}</small>
            <strong>{v}</strong>
          </span>
        </div>
      ))}
    </section>
  );
}

function OptionCard({ o, selected, recommended, disabled, onSelect }: { o: ProposalOption; selected: boolean; recommended: boolean; disabled: boolean; onSelect: () => void }) {
  if (!o.available) {
    return (
      <div className="pp-option is-unavailable">
        {o.tier && <span className="pp-tier">{o.tier}</span>}
        <h3>{o.name}</h3>
        <p className="pp-muted">Not available for your home's heat loss.</p>
      </div>
    );
  }
  const hp = o.heatPump ?? {};
  return (
    <button type="button" className={`pp-option${selected ? " is-selected" : ""}${recommended ? " is-recommended" : ""}`} onClick={onSelect} disabled={disabled} aria-pressed={selected}>
      <div className="pp-option-head">
        {o.tier && <span className="pp-tier">{o.tier}</span>}
        {recommended && <span className="pp-recommended">Recommended</span>}
      </div>
      <h3>{o.name}</h3>
      <p className="pp-option-kit">
        {hp.name ?? `${hp.brand ?? ""} ${hp.model ?? ""}`}
        {hp.kw ? ` · ${hp.kw} kW` : ""}
        {o.cylinder?.litres ? ` · ${o.cylinder.litres} L cylinder` : ""}
      </p>
      <ul className="pp-option-specs">
        {hp.scop35 ? (
          <li>
            <Zap size={14} aria-hidden="true" /> SCOP {hp.scop35} (35 °C){hp.scop55 ? ` · ${hp.scop55} (55 °C)` : ""}
          </li>
        ) : null}
        {hp.refrigerant ? (
          <li>
            <Leaf size={14} aria-hidden="true" /> {hp.refrigerant} refrigerant
          </li>
        ) : null}
        {hp.soundPowerDb ? (
          <li>
            <Thermometer size={14} aria-hidden="true" /> {hp.soundPowerDb} dB(A) sound power
          </li>
        ) : null}
        {(o.highlights ?? []).map((h) => (
          <li key={h}>
            <Check size={14} aria-hidden="true" /> {h}
          </li>
        ))}
      </ul>
      {o.description && <p className="pp-option-desc">{o.description}</p>}
      <div className="pp-option-price">
        <span className="pp-price">{gbp(o.totals?.payable)}</span>
        <span className="pp-muted">{o.totals?.grant ? `after the ${gbp(o.totals.grant)} grant` : "including VAT"}</span>
      </div>
      <span className="pp-option-cta">{selected ? <><Check size={15} aria-hidden="true" /> Selected</> : "Choose this option"}</span>
    </button>
  );
}

function PriceTable({ option, display }: { option: ProposalOption; display: Proposal["priceDisplay"] }) {
  if (display === "itemised") {
    return (
      <table className="pp-table">
        <tbody>
          {(option.lines ?? []).map((l, i) => (
            <tr key={i}>
              <td>
                {l.label}
                {l.qty && l.qty > 1 ? ` × ${l.qty}` : ""}
              </td>
              <td>{gbp((l.net ?? 0) + (l.vat ?? 0))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  // Grouped (default) and total: what's included, with group subtotals for grouped
  const byGroup = new Map<string, string[]>();
  for (const l of option.lines ?? []) {
    if (l.group === "discount") continue;
    byGroup.set(l.group, [...(byGroup.get(l.group) ?? []), `${l.label}${l.qty && l.qty > 1 ? ` × ${l.qty}` : ""}`]);
  }
  return (
    <div className="pp-groups">
      {display === "grouped"
        ? (option.groups ?? []).map((g) => (
            <div key={g.group} className="pp-group">
              <div className="pp-group-head">
                <span>{g.label}</span>
                <span>{gbp(g.net + g.vat)}</span>
              </div>
              {g.group !== "discount" && (
                <ul>
                  {(byGroup.get(g.group) ?? []).map((t, i) => (
                    <li key={i}>{t}</li>
                  ))}
                </ul>
              )}
            </div>
          ))
        : [...byGroup.entries()].map(([g, items]) => (
            <ul key={g} className="pp-group">
              {items.map((t, i) => (
                <li key={i}>{t}</li>
              ))}
            </ul>
          ))}
    </div>
  );
}

function Finance({
  finance,
  payable,
  payment,
  setPayment,
  months,
  setMonths,
  locked,
}: {
  finance: NonNullable<Proposal["finance"]>;
  payable: number;
  payment: "cash" | "finance";
  setPayment: (p: "cash" | "finance") => void;
  months: number | null;
  setMonths: (m: number) => void;
  locked: boolean;
}) {
  const deposit = Math.round(payable * finance.depositPercent) / 100;
  const loan = payable - deposit;
  const eligible = loan >= finance.minLoan && (!finance.maxLoan || loan <= finance.maxLoan);
  const term = months ?? finance.termsMonths[0];
  const monthly = monthlyRepayment(loan, finance.aprPercent, term);
  return (
    <div className="pp-finance">
      <h3>
        <PoundSterling size={16} aria-hidden="true" /> Spread the cost
      </h3>
      {!eligible ? (
        <p className="pp-muted">Finance is available for loans from {gbp(finance.minLoan)}{finance.maxLoan ? ` to ${gbp(finance.maxLoan)}` : ""}.</p>
      ) : (
        <>
          <div className="pp-seg" role="radiogroup" aria-label="How would you like to pay?">
            <button type="button" role="radio" aria-checked={payment === "cash"} className={payment === "cash" ? "is-on" : ""} disabled={locked} onClick={() => setPayment("cash")}>
              Pay in full
            </button>
            <button type="button" role="radio" aria-checked={payment === "finance"} className={payment === "finance" ? "is-on" : ""} disabled={locked} onClick={() => setPayment("finance")}>
              Finance
            </button>
          </div>
          {payment === "finance" && (
            <>
              <label className="pp-label" htmlFor="pp-term">
                Term
              </label>
              <select id="pp-term" className="pp-input" value={term} disabled={locked} onChange={(e) => setMonths(Number(e.target.value))}>
                {finance.termsMonths.map((m) => (
                  <option key={m} value={m}>
                    {m} months ({m / 12} years)
                  </option>
                ))}
              </select>
              <p className="pp-monthly">
                <span className="pp-price">{gbp(Math.round(monthly * 100) / 100)}</span> a month
              </p>
              {/* Representative example: the CONC 3.5.5R items, equally prominent */}
              <p className="pp-rep">
                Representative example: cash price {gbp(payable)}, deposit {gbp(deposit)}, amount of credit {gbp(loan)}, {term} monthly repayments of{" "}
                {gbp(Math.round(monthly * 100) / 100)}, total amount payable {gbp(Math.round((monthly * term + deposit) * 100) / 100)}, representative {finance.aprPercent}% APR
                (fixed){finance.lender ? `. Lender: ${finance.lender}` : ""}.
              </p>
              {finance.notice && <p className="pp-rep">{finance.notice}</p>}
            </>
          )}
        </>
      )}
    </div>
  );
}

function RunningCostsCard({
  rc,
  tariffs,
  tariffId,
  setTariffId,
  note,
}: {
  rc: NonNullable<ProposalOption["runningCosts"]>;
  tariffs: { id: string; name: string }[];
  tariffId: string | null;
  setTariffId: (t: string) => void;
  note: string;
}) {
  const t = rc.tariffs.find((x) => x.id === tariffId) ?? rc.tariffs[0];
  if (!t) return null;
  const fuel = label(FUELS, rc.current.fuel as never) || rc.current.fuel;
  const saving = rc.current.annualCost - t.annualCost;
  return (
    <section className="pp-section pp-card">
      <div className="pp-rc-head">
        <div>
          <h2>What it costs to run</h2>
          <p className="pp-sub">A year's heating and hot water for your home, compared with your {fuel.toLowerCase()} heating today.</p>
        </div>
        {tariffs.length > 1 && (
          <label className="pp-rc-tariff">
            <span className="pp-label">Electricity tariff</span>
            <select className="pp-input" value={t.id} onChange={(e) => setTariffId(e.target.value)}>
              {tariffs.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <div className="pp-rc">
        <div className="pp-rc-box">
          <small>Your {fuel.toLowerCase()} heating today</small>
          <strong>{gbp(rc.current.annualCost)}</strong>
          <span>a year</span>
        </div>
        <div className="pp-rc-box is-hp">
          <small>Heat pump on {t.name}</small>
          <strong>{gbp(t.annualCost)}</strong>
          <span>a year</span>
        </div>
        <div className={`pp-rc-box ${saving >= 0 ? "is-save" : "is-more"}`}>
          <small>{saving >= 0 ? "Estimated saving" : "Estimated extra cost"}</small>
          <strong>{gbp(Math.abs(saving))}</strong>
          <span>a year</span>
        </div>
        <div className="pp-rc-box is-co2">
          <small>Carbon saved</small>
          <strong>{(rc.co2.savedKg / 1000).toLocaleString("en-GB", { maximumFractionDigits: 1 })} t</strong>
          <span>CO₂ a year</span>
        </div>
      </div>
      <details className="pp-how">
        <summary>
          How we worked this out <ChevronDown size={14} aria-hidden="true" />
        </summary>
        <ul>
          <li>
            Space heating: {rc.spaceKwh.toLocaleString("en-GB")} kWh a year, from {rc.spaceSource}.
          </li>
          <li>
            Hot water: {rc.waterKwh.toLocaleString("en-GB")} kWh a year, from {rc.waterSource}.
          </li>
          <li>
            Heat pump efficiency {rc.scop} for heating ({rc.scopSource}) and {rc.hotWaterScop} for hot water: {rc.heatPumpKwh.toLocaleString("en-GB")} kWh of electricity a
            year at an average {t.pencePerKwh}p per kWh on {t.name}.
          </li>
          <li>
            Today: {rc.current.fuelKwh.toLocaleString("en-GB")} kWh of {fuel.toLowerCase()} at {rc.current.efficiencyPercent}% boiler efficiency and {rc.current.pencePerKwh}p per kWh
            {rc.current.standingSaved ? `, plus the ${gbp(rc.current.standingSaved)} gas standing charge you'd no longer pay` : ""}.
          </li>
        </ul>
        {note && <p className="pp-muted">{note}</p>}
      </details>
    </section>
  );
}

function HeatLossCard({ hl, option }: { hl: NonNullable<Proposal["heatLoss"]>; option?: ProposalOption }) {
  return (
    <section className="pp-section pp-card">
      <h2>
        <Thermometer size={20} aria-hidden="true" /> How we sized your heat pump
      </h2>
      <p className="pp-sub">
        {hl.fromSurvey
          ? `Your home loses ${hl.kw} kW on a cold day (from our survey${hl.surveyNote ? `: ${hl.surveyNote}` : ""}).`
          : `Your home is estimated to lose ${hl.kw} kW on a cold day (${hl.externalC} °C outside, ${hl.internalC} °C inside, the design temperatures for ${hl.designLocation}).`}
        {option?.heatPump?.kw ? ` The ${option.heatPump.kw} kW heat pump covers it.` : ""}
      </p>
      {!hl.fromSurvey && hl.elements.length > 0 && (
        <details className="pp-how">
          <summary>
            See the full calculation <ChevronDown size={14} aria-hidden="true" />
          </summary>
          <p className="pp-muted">
            {hl.method}. Each U-value comes from the published RdSAP 10 tables applied to your home's energy performance certificate. It's a guide: we confirm the final design
            with a room-by-room survey.
          </p>
          <div className="pp-table-scroll">
            <table className="pp-table pp-hl-table">
              <thead>
                <tr>
                  <th>Element</th>
                  <th>Area m²</th>
                  <th>U-value</th>
                  <th>W/K</th>
                </tr>
              </thead>
              <tbody>
                {hl.elements.map((e, i) => (
                  <tr key={i}>
                    <td>
                      {e.element}
                      {e.source && <small>{e.source}</small>}
                    </td>
                    <td>{e.area ?? ""}</td>
                    <td>{e.u ?? ""}</td>
                    <td>{e.wPerK}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </section>
  );
}

function AcceptPanel({
  preview,
  optionName,
  subjectToSurvey,
  payable,
  acceptanceText,
  onAccept,
  onDecline,
}: {
  preview?: boolean;
  optionName: string;
  subjectToSurvey: boolean;
  payable: number;
  acceptanceText: string;
  onAccept: (name: string, agree: boolean) => Promise<void>;
  onDecline?: (reason: string) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState("");

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (preview) return;
    if (name.trim().length < 2) return setError("Type your full name to sign.");
    if (!agree) return setError("Please tick to confirm.");
    setBusy(true);
    setError(null);
    try {
      await onAccept(name.trim(), agree);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function decline() {
    if (preview || !onDecline) return;
    setBusy(true);
    setError(null);
    try {
      await onDecline(reason);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="pp-section pp-card pp-accept" id="accept">
      <h2>Happy to go ahead?</h2>
      <p className="pp-sub">
        You're accepting <strong>{optionName}</strong> for <strong>{gbp(payable)}</strong>.
      </p>
      {acceptanceText && <p className="pp-muted">{acceptanceText}</p>}
      <form onSubmit={(e) => void submit(e)} className="pp-accept-form">
        <label className="pp-label" htmlFor="pp-sign">
          Type your full name to sign
        </label>
        <input id="pp-sign" className="pp-input pp-sign" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" disabled={preview || busy} placeholder="Your full name" />
        <label className="pp-agree">
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} disabled={preview || busy} />
          <span>{subjectToSurvey ? "I accept this quote, subject to a home survey, and its terms and conditions." : "I accept this quote and its terms and conditions."}</span>
        </label>
        {error && <p className="pp-error">{error}</p>}
        <button type="submit" className="pp-btn" disabled={preview || busy}>
          {busy ? "Sending…" : subjectToSurvey ? "Accept, subject to survey" : "Accept quote"}
        </button>
      </form>
      {onDecline && !preview && (
        <div className="pp-decline">
          {!declining ? (
            <button type="button" className="pp-link" onClick={() => setDeclining(true)}>
              Not for you? Let us know
            </button>
          ) : (
            <>
              <label className="pp-label" htmlFor="pp-reason">
                Anything we should know? (optional)
              </label>
              <textarea id="pp-reason" className="pp-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} />
              <button type="button" className="pp-btn pp-btn-ghost" onClick={() => void decline()} disabled={busy}>
                Decline this quote
              </button>
            </>
          )}
        </div>
      )}
    </section>
  );
}
